import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Check } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { createSubscriptionCheckout } from '../../services/paymentApi'
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'

export default function Pricing() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user, subscription } = useAuth()
  const [loading, setLoading] = useState<string | null>(null)

  useEffect(() => { document.title = `${t('pricing.title')} | ${t('common.appName')}` }, [t])

  const handleSubscribe = async (planId: 'plan_35' | 'plan_100') => {
    if (!user) {
      navigate(`/login?redirect=/pricing`)
      return
    }
    setLoading(planId)
    try {
      const { checkoutUrl } = await createSubscriptionCheckout(planId)
      window.location.href = checkoutUrl
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
      setLoading(null)
    }
  }

  const plans = [
    {
      id: 'free',
      name: t('pricing.freeName'),
      tagline: t('pricing.freeTagline'),
      price: t('pricing.freePrice'),
      priceNote: '',
      features: [
        t('pricing.features.singleDoc'),
        t('pricing.features.unlimitedSignatures'),
        t('pricing.features.realTimeTracking'),
        t('pricing.features.pdfExport'),
        t('pricing.features.excelExport'),
      ],
      ctaText: t('pricing.freeCtaText'),
      onCta: () => navigate('/create'),
      highlighted: false,
      currentPlan: false,
      disabled: false,
    },
    {
      id: 'one_time',
      name: t('pricing.payAsYouGo'),
      tagline: t('pricing.payPerDocTagline'),
      price: '$1.99',
      priceNote: t('pricing.oneTime'),
      features: [
        t('pricing.features.singleDoc'),
        t('pricing.features.unlimitedSignatures'),
        t('pricing.features.realTimeTracking'),
        t('pricing.features.pdfExport'),
        t('pricing.features.excelExport'),
        t('pricing.features.customLinks'),
      ],
      ctaText: t('pricing.payPerDoc'),
      onCta: () => navigate('/create'),
      highlighted: false,
      currentPlan: false,
      disabled: false,
    },
    {
      id: 'plan_35',
      name: t('pricing.starterName'),
      tagline: t('pricing.starterTagline'),
      price: '$20',
      priceNote: '/' + t('pricing.month'),
      features: [
        t('pricing.features.docsPerMonth', { count: 35 }),
        t('pricing.features.unlimitedSignatures'),
        t('pricing.features.realTimeTracking'),
        t('pricing.features.pdfExport'),
        t('pricing.features.excelExport'),
        t('pricing.features.customLinks'),
        t('pricing.features.creditValidity'),
        t('pricing.features.save14'),
      ],
      ctaText: loading === 'plan_35' ? t('common.loading') : t('pricing.subscribe'),
      onCta: () => handleSubscribe('plan_35'),
      highlighted: true,
      currentPlan: subscription?.planId === 'plan_35',
      disabled: loading !== null,
    },
    {
      id: 'plan_100',
      name: t('pricing.proName'),
      tagline: t('pricing.proTagline'),
      price: '$50',
      priceNote: '/' + t('pricing.month'),
      features: [
        t('pricing.features.docsPerMonth', { count: 100 }),
        t('pricing.features.unlimitedSignatures'),
        t('pricing.features.realTimeTracking'),
        t('pricing.features.pdfExport'),
        t('pricing.features.excelExport'),
        t('pricing.features.customLinks'),
        t('pricing.features.creditValidity'),
        t('pricing.features.save66'),
      ],
      ctaText: loading === 'plan_100' ? t('common.loading') : t('pricing.subscribe'),
      onCta: () => handleSubscribe('plan_100'),
      highlighted: false,
      currentPlan: subscription?.planId === 'plan_100',
      disabled: loading !== null,
    },
  ]

  const highlightedIdx = plans.findIndex((p) => p.highlighted)

  return (
    <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="page-header">
          <button className="btn btn-outline btn-icon" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <FontAwesomeIcon icon="arrow-left" />
          </button>
          <div>
            <h1>{t('pricing.title')}</h1>
            <p className="text-gray">{t('pricing.subtitle')}</p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '0',
            border: '1px solid var(--gray-200)',
            borderRadius: '0.75rem',
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {plans.map((plan, idx) => {
            const isHighlighted = idx === highlightedIdx
            const isLast = idx === plans.length - 1

            return (
              <div
                key={plan.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '2rem 1.5rem 1.5rem',
                  position: 'relative',
                  borderRight: !isLast && !isHighlighted ? '1px solid var(--gray-200)' : undefined,
                  ...(isHighlighted ? {
                    border: '2px solid var(--primary)',
                    borderRadius: '0.75rem',
                    margin: '-1px',
                    zIndex: 10,
                    background: 'var(--bg)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  } : {}),
                  ...(plan.currentPlan ? {
                    border: '2px solid var(--primary)',
                    borderRadius: '0.75rem',
                    margin: '-1px',
                    zIndex: 9,
                  } : {}),
                }}
              >
                {isHighlighted && (
                  <div style={{
                    position: 'absolute',
                    top: '-0.875rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 20,
                  }}>
                    <span style={{
                      background: 'var(--primary)',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      padding: '0.375rem 1rem',
                      borderRadius: '9999px',
                      whiteSpace: 'nowrap',
                    }}>
                      {t('pricing.popular')}
                    </span>
                  </div>
                )}

                {plan.currentPlan && (
                  <div style={{
                    position: 'absolute',
                    top: '-0.875rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 20,
                  }}>
                    <span style={{
                      background: 'var(--gray-600)',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      padding: '0.375rem 1rem',
                      borderRadius: '9999px',
                      whiteSpace: 'nowrap',
                    }}>
                      {t('pricing.currentPlan')}
                    </span>
                  </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.25rem' }}>{plan.name}</h3>
                  <p className="text-sm text-gray" style={{ marginBottom: '1.25rem' }}>{plan.tagline}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
                    <span style={{ fontSize: '2.5rem', fontWeight: 700 }}>{plan.price}</span>
                    {plan.priceNote && <span className="text-sm text-gray">{plan.priceNote}</span>}
                  </div>
                </div>

                <ul style={{ flex: 1, listStyle: 'none', padding: 0, margin: '0 0 1.5rem 0' }}>
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
                      <Check size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`btn ${isHighlighted ? 'btn-primary' : 'btn-success'} w-full`}
                  onClick={plan.onCta}
                  disabled={plan.disabled || plan.currentPlan}
                  style={{ marginTop: 'auto' }}
                >
                  {plan.currentPlan ? t('pricing.currentPlan') : plan.ctaText}
                </button>
              </div>
            )
          })}
        </div>

        <div className="text-center mt-4">
          <p className="text-sm text-gray">{t('pricing.creditNote')}</p>
        </div>
      </div>
    </div>
  )
}
